import type {
  TtsOnboardingActiveStepType,
  TtsOnboardingChecklistState,
  TtsOnboardingResolveChecklistInput
} from 'model/types/tts-onboarding';

/** Share of overall onboarding already done before these checklist items (the bar fills the remainder to 100%). */
const ONBOARDING_PROGRESS_PRIOR_PERCENT = 85;
const ONBOARDING_PROGRESS_CHECKLIST_PERCENT = 15;

export function pickActiveOnboardingStep(
  needsPlan: boolean,
  needsPayment: boolean,
  needsPermissions: boolean
): TtsOnboardingActiveStepType | null {
  if (needsPlan) return 'plan';
  if (needsPayment) return 'payment';
  if (needsPermissions) return 'permissions';
  return null;
}

export function getTtsServiceAccountEmail(merchantName: string | undefined): string {
  const trimmed = merchantName?.trim();
  if (!trimmed) {
    return 'yourbrand.tts@trendio.ai';
  }
  const localPart = trimmed.replaceAll(/\s+/g, '_').toLowerCase();
  return `${localPart}.tts@trendio.ai`;
}

export function resolveChecklistCompletion(input: TtsOnboardingResolveChecklistInput): TtsOnboardingChecklistState {
  let permissionsDone = input.ttsPermissionsGranted === true;
  let planDone = Boolean(input.tiktokPlan) || input.hidePlans;
  let paymentMethodsCount = input.paymentMethodsLength;

  return { permissionsDone, planDone, paymentMethodsCount };
}

export function computeNeedsFlags(
  hasMerchant: boolean,
  hidePlans: boolean,
  checklist: TtsOnboardingChecklistState
): Readonly<{ needsPayment: boolean; needsPlan: boolean; needsPermissions: boolean }> {
  if (!hasMerchant) return { needsPayment: true, needsPlan: true, needsPermissions: true };

  const needsPermissions = hasMerchant && !checklist.permissionsDone;
  const needsPlan = hasMerchant && !hidePlans && !checklist.planDone;
  const needsPayment = hasMerchant && !hidePlans && checklist.paymentMethodsCount === 0;

  return { needsPayment, needsPlan, needsPermissions };
}

export function computeProgressPercent(
  hidePlans: boolean,
  checklist: TtsOnboardingChecklistState,
  needsPlan: boolean,
  needsPayment: boolean
): number {
  let progressTotal = 0;
  let progressComplete = 0;
  progressTotal += 1;
  if (checklist.permissionsDone) progressComplete += 1;

  const includePlanTrack = !hidePlans || needsPlan;
  const includePaymentTrack = !hidePlans || needsPayment;

  if (includePlanTrack) {
    progressTotal += 1;
    if (checklist.planDone) progressComplete += 1;
  }
  if (includePaymentTrack) {
    progressTotal += 1;
    if (checklist.paymentMethodsCount > 0) progressComplete += 1;
  }

  return Math.min(
    100,
    Math.round(ONBOARDING_PROGRESS_PRIOR_PERCENT + (ONBOARDING_PROGRESS_CHECKLIST_PERCENT * progressComplete) / progressTotal)
  );
}
